import { useCallback, useMemo } from 'react';
import { fetchJson } from '@helpCenter/util/fetch/fetch';
import { di } from 'react-magnetic-di';
import type { AgentMessage, ConversationProvider, HelpseekerMessage, HelpseekerMessageBody } from '../../common/types';
import type { CreateVirtualAgentConversationResponse, VirtualAgentConversationMessageResponse } from './types';
import { ChannelType, ExternalSource } from './types';

export const useVirtualAgentConversationV2Provider = ({
    contextId,
    cloudId,
    isTestConversation,
    channelType,
    baseUrl = '',
}: {
    contextId: string;
    cloudId: string;
    isTestConversation: boolean | undefined;
    channelType: ChannelType;
    baseUrl?: string;
}): ConversationProvider => {
    di(fetchJson);

    const startConversation = useCallback(
        async (
            abortSignal: AbortSignal,
            message?: HelpseekerMessageBody
        ): Promise<CreateVirtualAgentConversationResponse | undefined> => {
            try {
                const requestMessage = message
                    ? {
                          message: {
                              content: JSON.stringify(message.content),
                          },
                      }
                    : null;
                const response = await fetchJson<CreateVirtualAgentConversationResponse>(
                    `${baseUrl}/gateway/api/jsm/virtual-agent/cloudId/${cloudId}/public/api/v2/web/conversation`,
                    {
                        signal: abortSignal,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            channelType,
                            contextId,
                            createdAt: new Date().toISOString(),
                            isTestConversation: isTestConversation === true,
                            ...requestMessage,
                        }),
                        method: 'POST',
                    }
                );
                return response;
            } catch (error: unknown) {
                if (error instanceof DOMException) {
                    // TODO: add analytics for this
                    return undefined;
                }
                throw error;
            }
        },
        [baseUrl, cloudId, isTestConversation, channelType, contextId]
    );

    const sendMessage = useCallback(
        async (message: HelpseekerMessage, abortSignal: AbortSignal): Promise<AgentMessage[]> => {
            try {
                const response = await fetchJson<VirtualAgentConversationMessageResponse[]>(
                    `${baseUrl}/gateway/api/jsm/virtual-agent/cloudId/${cloudId}/public/api/v2/web/conversation/message`,
                    {
                        signal: abortSignal,
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            externalSource:
                                channelType === ChannelType.HelpCenter
                                    ? ExternalSource.Orchestrator
                                    : ExternalSource.Portal,
                            message: {
                                content: JSON.stringify(message.message.content),
                                optionId: message.message.optionId,
                                jiraField: message.message.jiraField,
                            },
                            timeCreated: message.timeCreated,
                            conversationChannelId: message.conversationChannelId,
                        }),
                        method: 'POST',
                    }
                );

                return response;
            } catch (error: unknown) {
                if (error instanceof DOMException) {
                    // TODO: add analytics for this
                    return [];
                }
                throw error;
            }
        },
        [baseUrl, channelType, cloudId]
    );

    return useMemo(() => ({ startConversation, sendMessage }), [sendMessage, startConversation]);
};
